import { ComplianceTrendData } from "@/interfaces/Charts";
import {
  Audit,
  Driver,
  Fleet,
  ResponseAuditSaveQuestions,
  SaveAuditQuestionsDTO,
  SaveQuestionsForCorporateDTO,
} from "@/interfaces/Company";
import {
  CheckListDTO,
  CreateFastAudit,
  AuditQuestions,
  AuditQuestionsGroup,
  RadarData,
  TotalReport,
  AuditChecklistResponse,
} from "@/interfaces/Audit";
import axiosBaseQuery from "@/utils/axiosBaseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import { userService } from "./userService";
import { Notification } from "@/interfaces/Notification";
import { companyService } from "./companyService";

/**
 * IMPORTANTE:
 * Debido a una limitación actual, no se pueden exportar los hooks individualmente sin causar problemas de tipos.
 * El siguiente código es un intento de exportar el hook useLoginMutation, pero resulta en el siguiente error de TypeScript:
 * "El tipo inferido de 'useLoginMutation' no se puede nombrar sin una referencia a '../../../node_modules/@reduxjs/toolkit/dist/query/react/buildHooks'. Es probable que esto no sea portátil. Es necesaria una anotación de tipo.ts(2742)"
 * en su lugar llamar directamente a auditService y acceder al hook
 * @example
  auditService.findAll(...params)
 */
export const auditService = createApi({
  reducerPath: "auditApi",
  baseQuery: axiosBaseQuery,
  tagTypes: [
    "AuditQuestions",
    "AuditGeneralData",
    "AuditTableReport",
    "AuditChartReport",
    "AuditTotalTableReport",
    "Notification",
  ],
  endpoints: (builder) => ({
    findById: builder.query<
      { audit: Audit },
      { auditId: number; corporate_group?: number }
    >({
      query: ({ auditId, corporate_group }) => ({
        url: `/audit/${auditId}`,
        method: "GET",
        params: {
          corporate_group: corporate_group,
        },
      }),
      providesTags: ["AuditGeneralData"],
    }),
    findQuestionsByCompanySize: builder.query<
      AuditQuestions[],
      { companyId: number }
    >({
      query: ({ companyId }) => ({
        url: `/audit/questions/company-size/?company=${companyId}&group_by_step=true`,
        method: "GET",
      }),
    }),
    findQuestionsByCompanysizeGrouped: builder.query<
      AuditQuestionsGroup[],
      { companyId: number; auditId?: number }
    >({
      query: ({ companyId, auditId }) => ({
        url: `/audit/questions/company-size/?company=${companyId}&audit=${auditId}&group_by_step=true`,
        method: "GET",
      }),
      providesTags: ["AuditQuestions"],
    }),
    radarChart: builder.query<
      RadarData[],
      { companyId: number; auditId?: number }
    >({
      query: ({ companyId, auditId }) => ({
        url: `/audit/radar-chart/?audit=${auditId}&company=${companyId}`,
        method: "GET",
      }),
      transformResponse: (response: { total_table_report?: RadarData[] }) => {
        return response?.total_table_report || [];
      },
      providesTags: ["AuditChartReport"],
    }),
    tableReportTotal: builder.query<
      TotalReport,
      { companyId: number; auditId?: number }
    >({
      query: ({ companyId, auditId }) => ({
        url: `/audit/table-report-total/?audit=${auditId}&company=${companyId}`,
        method: "GET",
      }),
      transformResponse: (response: {
        total_table_report?: TotalReport;
      }): TotalReport => {
        return response?.total_table_report ?? { counts: [], general: 0 };
      },
      providesTags: ["AuditTotalTableReport"],
    }),
    tableReport: builder.query<any, { companyId: number; auditId?: number }>({
      query: ({ companyId, auditId }) => ({
        url: `/audit/table-report/?audit=${auditId}&company=${companyId}`,
        method: "GET",
      }),
      transformResponse: (response: { table_report?: any[] }) => {
        return response?.table_report || [];
      },
      providesTags: ["AuditTableReport"],
    }),
    saveAudit: builder.mutation<AuditChecklistResponse, CheckListDTO>({
      query: (dataAudit) => ({
        url: `/audit/save-process-checklist/`,
        method: "POST",
        data: {
          ...dataAudit,
        },
      }),
      invalidatesTags: [
        "AuditTableReport",
        "AuditTotalTableReport",
        "AuditChartReport",
        "AuditGeneralData",
      ],
    }),
    generateReport: builder.mutation<
      { file: string },
      {
        // companyId: number;
        format_to_save: string;
        auditId?: number;
      }
    >({
      // query: ({ companyId, format_to_save, auditId }) => ({
      query: ({ format_to_save, auditId }) => ({
        url: `/audit/${auditId}/company/`,
        method: "POST",
        data: {
          // company: companyId,
          format_type: format_to_save,
          // audit: auditId,
        },
      }),
    }),
    generateWorkPlan: builder.mutation<
      { file: string },
      {
        // companyId: number;
        format_to_save: string;
        auditId?: number;
      }
    >({
      // query: ({ companyId, format_to_save, auditId }) => ({
      query: ({ format_to_save, auditId }) => ({
        url: `/audit/${auditId}/workplan/`,
        method: "POST",
        data: {
          // company: companyId,
          format_type: format_to_save,
          // audit: auditId,
        },
      }),
    }),
    findFleetsByCompanyId: builder.query<
      Fleet[],
      { companyId: number; auditId?: number; corporate_group?: number }
    >({
      query: ({ auditId }) => ({
        url: `/audit/${auditId}/counter`,
        method: "GET",
      }),
      transformResponse: (response: {
        audit_count: { fleets_count: Fleet[] };
      }) => response.audit_count.fleets_count,
    }),
    findDriversByCompanyId: builder.query<
      Driver[],
      { companyId: number; auditId?: number; corporate_group?: number }
    >({
      query: ({ auditId }) => ({
        url: `/audit/${auditId}/counter`,
        method: "GET",
      }),
      transformResponse: (response: {
        audit_count: { drivers_count: Driver[] };
      }) => response.audit_count.drivers_count,
    }),
    saveAnswerCuestions: builder.mutation<
      { audit_count: ResponseAuditSaveQuestions },
      SaveAuditQuestionsDTO
    >({
      query: ({ auditId, ...rest }) => ({
        url: `/audit/${auditId}/counter`,
        method: "POST",
        data: {
          ...rest,
        },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(userService.util.invalidateTags(["FindUserById"]));
      },
    }),
    initAuditCorporate: builder.mutation<Audit, { corporate: number }>({
      query: ({ corporate }) => ({
        url: `/audit/init_audit_corporate/`,
        method: "POST",
        data: {
          corporate: corporate,
        },
      }),
    }),
    saveCountForCompanyInCorporate: builder.mutation<
      ResponseAuditSaveQuestions,
      SaveQuestionsForCorporateDTO
    >({
      query: (questionsDTO) => ({
        url: `/audit/save_count_for_company_in_corporate/`,
        method: "POST",
        data: {
          ...questionsDTO,
        },
      }),
    }),
    updateAudit: builder.mutation<Audit, Partial<Audit>>({
      query: ({ id, ...rest }) => ({
        url: `/audit/${id}`,
        method: "PATCH",
        data: {
          ...rest,
        },
      }),
      invalidatesTags: ["AuditQuestions", "AuditGeneralData"],
    }),
    countAuditsByConsultor: builder.query<any, { consultor_id: number }>({
      query: ({ consultor_id }) => ({
        url: `/audit/count_audits_by_consultor`,
        method: "GET",
        params: {
          consultor: consultor_id,
        },
      }),
    }),
    countAuditsByConsultorByType: builder.query<any, { consultor_id: number }>({
      query: ({ consultor_id }) => ({
        url: `/audit/consultant/${consultor_id}/counter/?type=true`,
        method: "GET",
      }),
    }),
    countAuditsByConsultorByModeEjecution: builder.query<
      any,
      { consultor_id?: number }
    >({
      query: ({ consultor_id }) => ({
        url: `/audit/consultant/${consultor_id}/counter/?mode_ejecution=true`,
        method: "GET",
      }),
    }),
    countAuditsByConsultors: builder.query<any, void>({
      query: () => ({
        url: `/audit/count/?action=by_consultant`,
        method: "GET",
      }),
    }),
    complianceTrend: builder.query<{ trend: ComplianceTrendData[] }, void>({
      query: () => ({
        url: `/audit/count/?action=compliance_trend`,
        method: "GET",
      }),
    }),
    sendReport: builder.mutation<
      string,
      {
        email_to: string;
        audit: number;
        company: number;
        schedule: number;
        sequence: number;
      }
    >({
      query: ({ email_to, audit, company, schedule, sequence }) => ({
        url: `/audit/send-report/`,
        method: "POST",
        data: {
          email_to: email_to,
          audit: audit,
          company: company,
          schedule: schedule,
          sequence: sequence,
        },
      }),
    }),
    findNotificationsByUser: builder.query<
      Notification[] | [],
      {
        user: number;
      }
    >({
      query: ({ user }) => ({
        url: `/audit/find_notifications_by_user`,
        method: "GET",
        params: {
          user: user,
        },
      }),
      providesTags: ["Notification"],
    }),
    readNotifications: builder.mutation<
      { message: boolean },
      {
        notifications: number[];
      }
    >({
      query: ({ notifications }) => ({
        url: `/audit/read_notifications/`,
        method: "PATCH",
        data: {
          notifications: notifications,
        },
      }),
      invalidatesTags: ["Notification"],
    }),
    saveFastAudit: builder.mutation<Audit, CreateFastAudit>({
      query: (createFastAudit) => ({
        url: "/audit/",
        method: "POST",
        data: createFastAudit,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(companyService.util.invalidateTags(["GetCompamies"]));
      },
    }),
  }),
});
